<script lang="ts" setup>
import { NButton } from 'naive-ui'
import ActionTrigger from './ActionTrigger.vue'
import {ActionMenuItem, type MenuOptionsProps} from '~/composables/useActionMenu'
import { IconsDelete } from "#components";

const props = withDefaults(defineProps<MenuOptionsProps>(), {
  position: 'bottom'
})

const isOpen = ref(false)
const currentLoadingKey = ref("")
const triggerRef = ref<HTMLElement>()
const menuRef = ref<HTMLElement>()

// Dynamic teleport target
const { setCurrentElement, teleportTarget } = useTeleportTarget()

const emit = defineEmits<{
  select: [key: ActionMenuItem, rowData: any]
}>()

function toggleMenu() {
  if (currentLoadingKey.value) {
    return
  }
  
  if (!isOpen.value) {
    const event = new CustomEvent('menuOpening', { detail: props.rowId })
    document.dispatchEvent(event)
  }
  isOpen.value = !isOpen.value
}

function closeMenu() {
  if (currentLoadingKey.value) {
    return
  }
  isOpen.value = false
}

const positionTrigger = ref(0)

const menuStyle = computed(() => {
  positionTrigger.value
  
  if (!triggerRef.value || !isOpen.value) return {}
  
  const triggerRect = triggerRef.value.getBoundingClientRect()
  
  // Get the container element (teleport target)
  const container = typeof teleportTarget.value === 'string' ? 
    document.body : 
    teleportTarget.value as HTMLElement
  
  let containerRect = { left: 0, top: 0 }
  if (container && container !== document.body) {
    containerRect = container.getBoundingClientRect()
  }
  
  // Calculate position relative to container
  const relativeX = triggerRect.left - containerRect.left
  const relativeY = triggerRect.top - containerRect.top
  
  // Calculate horizontal offset based on xShift
  const xOffset = props.xShift !== undefined ? props.xShift : -50 // Default to center (-50%)
  const leftPosition = relativeX + (triggerRect.width / 2) // Start from center of trigger
  
  if (props.position === 'top') {
    return {
      position: container === document.body ? 'fixed' : 'absolute',
      left: `${leftPosition}px`,
      bottom: `${container.clientHeight - relativeY + 8}px`,
      transform: `translateX(${xOffset}%)`
    }
  } else {
    return {
      position: container === document.body ? 'fixed' : 'absolute',
      left: `${leftPosition}px`,
      top: `${relativeY + triggerRect.height + 8}px`,
      transform: `translateX(${xOffset}%)`
    }
  }
})

// Update position on scroll
function updatePosition() {
  if (isOpen.value) {
    positionTrigger.value++
  }
}

function setLoadingKey(key: string = "") {
  currentLoadingKey.value = key;
}

async function handleSelect(option: MenuOption) {
  emit('select', option.key, props.rowData)

  if (option.cb) {
    setLoadingKey(`${props.rowId}-${option.key}`);
    await option.cb();
    setLoadingKey();
  }

  closeMenu()
}

onMounted(() => {
  // Set current element for teleport target detection
  if (triggerRef.value) {
    setCurrentElement(triggerRef.value)
  }
  
  // Listen for clicks outside to close menu
  document.addEventListener('click', (e) => {
    const target = e.target as Element
    // Don't close menu if we're in a loading state
    if (currentLoadingKey.value) {
      return
    }
    if (!target || !target.closest('.relative')) {
      closeMenu()
    }
  })
  
  // Listen for other menus opening
  const handleOtherMenuOpening = (event: CustomEvent) => {
    const openingMenuId = event.detail
    // Don't close this menu if we're in a loading state
    if (openingMenuId !== props.rowId && isOpen.value && !currentLoadingKey.value) {
    closeMenu()
    }
  }
  
  document.addEventListener('menuOpening', handleOtherMenuOpening as EventListener)
  document.addEventListener('scroll', updatePosition, true)
  window.addEventListener('resize', updatePosition)
  
  onUnmounted(() => {
    document.removeEventListener('menuOpening', handleOtherMenuOpening as EventListener)
    document.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
  })
})

function getLoadingIcon() {
  return h('svg', { 
    class: 'animate-spin w-4 h-4', 
    viewBox: '0 0 24 24', 
    fill: 'none' 
  }, [
    h('circle', { 
      class: 'opacity-25', 
      cx: '12', 
      cy: '12', 
      r: '10', 
      stroke: 'currentColor', 
      'stroke-width': '4' 
    }),
    h('path', { 
      class: 'opacity-75', 
      fill: 'currentColor', 
      d: 'M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' 
    })
  ])
}

function getMenuIcon(key: ActionMenuItem) {
  switch (key) {
    case ActionMenuItem.SEND_INVITE_REMINDER:
    case ActionMenuItem.SEND_TEAM_REMINDER:
      return h('svg', { width: '20', height: '20', viewBox: '0 0 20 20', fill: 'none' }, [
        h('path', { 
          d: 'M10.7333 1.99935C10.6439 2.4393 10.6439 2.89273 10.7333 3.33268H3.00725L8.04059 7.77401L11.4066 4.76068C11.6906 5.11202 12.0453 5.40468 12.4486 5.61735L8.04792 9.55801L2.66659 4.81002V12.666H13.3333V5.93268C13.7732 6.02208 14.2266 6.02208 14.6666 5.93268V13.3327C14.6666 13.5095 14.5963 13.6791 14.4713 13.8041C14.3463 13.9291 14.1767 13.9993 13.9999 13.9993H1.99992C1.82311 13.9993 1.65354 13.9291 1.52851 13.8041C1.40349 13.6791 1.33325 13.5095 1.33325 13.3327V2.66602C1.33325 2.4892 1.40349 2.31964 1.52851 2.19461C1.65354 2.06959 1.82311 1.99935 1.99992 1.99935H10.7333ZM13.9999 0.666016C14.2626 0.666016 14.5226 0.717747 14.7653 0.818257C15.0079 0.918766 15.2284 1.06608 15.4141 1.2518C15.5998 1.43752 15.7472 1.658 15.8477 1.90065C15.9482 2.1433 15.9999 2.40337 15.9999 2.66602C15.9999 2.92866 15.9482 3.18873 15.8477 3.43138C15.7472 3.67403 15.5998 3.89451 15.4141 4.08023C15.2284 4.26595 15.0079 4.41326 14.7653 4.51377C14.5226 4.61428 14.2626 4.66602 13.9999 4.66602C13.4695 4.66602 12.9608 4.4553 12.5857 4.08023C12.2106 3.70516 11.9999 3.19645 11.9999 2.66602C11.9999 2.13558 12.2106 1.62687 12.5857 1.2518C12.9608 0.876729 13.4695 0.666016 13.9999 0.666016Z',
          fill: 'black'
        })
      ])
    case ActionMenuItem.ENTER_TEAM_INFO:
    case ActionMenuItem.EDIT_TEAM_INFO:
    case ActionMenuItem.INVITE_JUDGE:
      return h('svg', { width: '20', height: '20', viewBox: '0 0 20 20', fill: 'none' }, [
        h('path', { 
          d: 'M9.33342 9.50068V10.894C8.73003 10.6807 8.08427 10.6153 7.45035 10.7032C6.81644 10.7912 6.21288 11.0299 5.69037 11.3995C5.16786 11.769 4.74164 12.2586 4.44752 12.827C4.1534 13.3954 3.99996 14.026 4.00008 14.666H2.66675C2.66644 13.8519 2.85252 13.0485 3.21073 12.3174C3.56894 11.5863 4.08976 10.9469 4.7333 10.4482C5.37683 9.94957 6.12599 9.60484 6.92335 9.44048C7.72072 9.27612 8.54514 9.29715 9.33342 9.50068ZM8.00008 8.66602C5.79008 8.66602 4.00008 6.87602 4.00008 4.66602C4.00008 2.45602 5.79008 0.666016 8.00008 0.666016C10.2101 0.666016 12.0001 2.45602 12.0001 4.66602C12.0001 6.87602 10.2101 8.66602 8.00008 8.66602ZM8.00008 7.33268C9.47342 7.33268 10.6667 6.13935 10.6667 4.66602C10.6667 3.19268 9.47342 1.99935 8.00008 1.99935C6.52675 1.99935 5.33342 3.19268 5.33342 4.66602C5.33342 6.13935 6.52675 7.33268 8.00008 7.33268ZM12.0001 11.3327V9.33268H13.3334V11.3327H15.3334V12.666H13.3334V14.666H12.0001V12.666H10.0001V11.3327H12.0001Z',
          fill: 'black'
        }),
      ])
    case ActionMenuItem.REMOVE_JUDGE:
      return h('svg', { width: '20', height: '20', viewBox: '0 0 20 20', fill: 'none' }, [
        h('path', { 
          d: 'M9.33342 9.50068V10.894C8.73003 10.6807 8.08427 10.6153 7.45035 10.7032C6.81644 10.7912 6.21288 11.0299 5.69037 11.3995C5.16786 11.769 4.74164 12.2586 4.44752 12.827C4.1534 13.3954 3.99996 14.026 4.00008 14.666H2.66675C2.66644 13.8519 2.85252 13.0485 3.21073 12.3174C3.56894 11.5863 4.08976 10.9469 4.7333 10.4482C5.37683 9.94957 6.12599 9.60484 6.92335 9.44048C7.72072 9.27612 8.54514 9.29715 9.33342 9.50068ZM8.00008 8.66602C5.79008 8.66602 4.00008 6.87602 4.00008 4.66602C4.00008 2.45602 5.79008 0.666016 8.00008 0.666016C10.2101 0.666016 12.0001 2.45602 12.0001 4.66602C12.0001 6.87602 10.2101 8.66602 8.00008 8.66602ZM8.00008 7.33268C9.47342 7.33268 10.6667 6.13935 10.6667 4.66602C10.6667 3.19268 9.47342 1.99935 8.00008 1.99935C6.52675 1.99935 5.33342 3.19268 5.33342 4.66602C5.33342 6.13935 6.52675 7.33268 8.00008 7.33268ZM10.0001 11.3327H15.3334V12.666H10.0001V11.3327Z',
          fill: 'black'
        }),
      ])
    case ActionMenuItem.MARK_COMPLETE:
      return h('svg', { width: '20', height: '20', viewBox: '0 0 20 20', fill: 'none' }, [
        h('path', {
          d: 'M6.66656 10.1137L12.7952 3.98633L13.7379 4.92899L6.66723 11.9997L2.42456 7.75699L3.36723 6.81433L6.66723 10.1143L6.66656 10.1137Z',
          fill: 'black'
        }),
      ])

    case ActionMenuItem.VIEW_PURCHASE:
      return h('svg', { width: '20', height: '20', viewBox: '0 0 20 20', fill: 'none' }, [
        h('path', {
          d: 'M2 10S5 4 10 4s8 6 8 6-3 6-8 6-8-6-8-6Z',
          stroke: 'currentColor',
          'stroke-width': '1.5',
        }),
        h('circle', {
          cx: '10',
          cy: '10',
          r: '2.5',
          stroke: 'currentColor',
          'stroke-width': '1.5',
        }),
      ])

    case ActionMenuItem.ADD_NOTE:
    case ActionMenuItem.EDIT_NOTE:
      return h('svg', { width: '18', height: '18', viewBox: "0 0 36 36" }, [
        h('path', { d: "M28 30H6V8h13.22l2-2H6a2 2 0 0 0-2 2v22a2 2 0 0 0 2 2h22a2 2 0 0 0 2-2V15l-2 2Z", stroke: 'black', 'stroke-width': '1'  }),
        h('path', { d: "m33.53 5.84l-3.37-3.37a1.61 1.61 0 0 0-2.28 0L14.17 16.26l-1.11 4.81A1.61 1.61 0 0 0 14.63 23a1.7 1.7 0 0 0 .37 0l4.85-1.07L33.53 8.12a1.61 1.61 0 0 0 0-2.28M18.81 20.08l-3.66.81l.85-3.63L26.32 6.87l2.82 2.82ZM30.27 8.56l-2.82-2.82L29 4.16L31.84 7Z", stroke: 'black', 'stroke-width': '1'  }),
        h('path', { d: "M0 0h36v36H0z", fill: 'none', 'stroke-width': '1'  }),
      ])

    case ActionMenuItem.DELETE_WINNER:
      return h(IconsDelete)

    default:
      return h('svg', { width: '16', height: '16', viewBox: '0 0 24 24', fill: 'none' }, [
        h('circle', { cx: '12', cy: '12', r: '10' }),
        h('line', { x1: '12', y1: '16', x2: '12', y2: '12' }),
        h('line', { x1: '12', y1: '8', x2: '12.01', y2: '8' })
      ])
  }
}
</script>

<template>
  <div ref="triggerRef" class="relative flex justify-center">
    <NButton
      text
      size="small"
      class="pr-2 action-trigger"
      @click="toggleMenu"
    >
      <ActionTrigger />
    </NButton>

    <Teleport :to="teleportTarget">
      <div
        v-if="isOpen"
        ref="menuRef"
        :class="[
          'p-1 bg-white border border-gray-200 rounded-2xl shadow-lg z-[9999] text-gray-900'
        ]"
        :style="menuStyle"
        style="width: max-content"
      >
      <button
        v-for="option in options"
        :key="option.key"
        :disabled="option.disabled"
        :class="[
          'action-menu-item w-full text-left px-3 py-2 text-sm rounded-[0.625rem] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-gray-900',
          option.key === ActionMenuItem.DELETE_WINNER && 'action-menu-item--danger',
          option.disabled && 'opacity-50 cursor-not-allowed',
        ]"
        @click="handleSelect(option)"
      >
        <div class="w-4 h-4 flex-shrink-0 action-menu-icon">
          <component v-if="currentLoadingKey === `${props.rowId}-${option.key}`" :is="getLoadingIcon()" />
          <component v-else :is="getMenuIcon(option.key)" />
        </div>
        <span>{{ option.label }}</span>
      </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.action-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 8px;
}

.action-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  color: #111827 !important; /* force dark text */
}

.action-menu-item:hover {
  background-color: #f3f4f6;
  color: #111827 !important; /* maintain dark text on hover */
}

.action-menu-item span {
  color: #111827 !important; /* force dark text for spans */
}

.action-menu-item--danger,
.action-menu-item--danger span,
.action-menu-item--danger :deep(svg) {
  color: #b83021 !important;
}

.action-menu-item--danger:hover:not(:disabled) {
  background-color: #fbe8e5 !important;
  color: #b83021 !important;
}

.action-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
